import React, { useState, useEffect } from 'react'
import Helmet from 'react-helmet'

import { ChevronDownIcon, Loader2 } from 'lucide-react'
import moment from 'moment'
import queryString from 'query-string'
import { Badge, Button, FormGroup, Input, Label, DefaultPopover, Separator, DefaultTooltip } from 'twyne-new.ui';

import { useGlobalStore } from '@Stores/global'
import appStore from 'stores/app'
import history from 'utils/history'

import DefaultTableResize from '../DefaultTableResize'
import InfiniteScroll from '../Grid/components/Table/InfiniteScroll'
import { useAppLayoutContext } from '../app-layout/context'
import { formatters } from '../hierarchical-report/utils'
import { ReportBody } from '../reports/body'

import Configure from './Configure'
import Header from './Header'

const MAX_ROWS = 1000

export default class Drilldown extends React.Component {
  /**
   * OVERRITE ATTRIBUTES
   * @property {store: singletonStore} - The default api store
   * @property {collection: string} - The name of the elastic collection
   * @property {report: string} - The name of the report
   * @property {fields: { default: array, options: array }} - The summary cards data
   * @property {ignoreCustomFields: boolean} - If true, ignore custom fields
   */

  state = {
    title: '',
    isLoading: false,

    selectedFields: null,
    fields: [],
    rows: [],
    count: 0,

    options: {
      page: 1,
      size: 25,
    },
    filters: {
      filterby: {},
      startDate: null,
      endDate: null,
    },
  }

  get columns() {
    const { state: { selectedFields }, fields: { default: _default, fieldsFormatters } } = this

    const fields = selectedFields || _default

    return fields.map(value => ({ dataField: value, text: formatters.groupsText(value), formatter: fieldsFormatters && fieldsFormatters[value] }))
  }

  get hasNextPage() {
    const { count, options: { size } } = this.state
    return count > size && size < MAX_ROWS
  }

  loadData = async () => {
    const { state: { filters, options, selectedFields } } = this
    const config = { ...filters, ...options, fields: selectedFields }
    this.setState({ isLoading: true })

    const { rows, count } = await this.store.drilldown(config)
    this.setState({ rows, count, isLoading: false })
  }

  parseURL = () => {
    const query = queryString.parse(history.location.search)
    if (query && query.configure) {
      return JSON.parse(atob(query.configure))
    } else {
      throw new Error()
    }
  }

  Export = () => {
    const [tooltipOpen, setTooltipOpen] = useState(false)
    const [show, setShow] = useState(false)
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(null)

    const handleExport = () => {
      setLoading(true)
      const { filters, selectedFields: fields, credential } = this.state
      const config = { fields, isExport: true, ...filters, credential }

      this.store.drilldown(config).then(data => {
        setLoading(false)
        setSuccess(true)
      }).finally(() => {
        setTooltipOpen(true)
        setShow(false)
      })

      setTimeout(() => {
        setTooltipOpen(false)
      }, 3000)
    }

    return <>
      <DefaultPopover
        wrapperProps={{ open: show, onOpenChange: setShow }}
        content
        trigger={<>
          <DefaultTooltip side='left' open={tooltipOpen} content={tooltipOpen && success ? "The file is being processed and will be sent to your email shortly. Please, check your email inbox." : "Something went wrong. Please, try again later."}>
            <Button variant='outline' size='lg'>
              {loading ? <Loader2 className='animate-spin' size='16' /> : <>
                Export
                <ChevronDownIcon size='16' />
              </>}
            </Button>
          </DefaultTooltip>
        </>}
      >
        <span className='text-md'>Export File</span>
        <Separator className='m-0 my-2' />
        <FormGroup>
          <Label>Zip Password</Label>
          <Input required name="credential" value={this.state.credential}
            onChange={e => this.setState({ credential: e.target.value })} />
        </FormGroup>
        <Button className='mt-2' color="accent" block onClick={handleExport}>Export</Button>
      </DefaultPopover>
    </>
  }

  FiltersBadges = () => {
    const { filterby } = this.state.filters
    const filters = Object.entries(filterby)

    return <div className='flex gap-2'>
      {filters.map(([key, value], index) => {
        value = value.join(', ')

        if ([null, undefined, ''].includes(value)) {
          value = 'empty'
        }

        if (['hour', 'day', 'month'].includes(key)) {
          value = value.split(', ')[0]
          value = moment.utc(value).tz(appStore.timezone).format(dateFormat[key])
        }

        return <Badge key={index} size='md' color='accent' variant='light'>{`${key}: ${value}`}</Badge>
      })}
    </div>
  }

  Actions = () => <>
    <div className="flex justify-between items-center p-4 border-b twy-border-base">
      <div className='flex items-center gap-2'>
        <strong>Filtered by:</strong>
        <this.FiltersBadges />
      </div>
      <div className='flex items-center gap-2'>
        <this.Configure />
        <this.Export />
      </div>
    </div>
  </>

  HeaderWrapper = () => {
    const { collection } = this
    const { title, filters: { startDate, endDate }, count, rows } = this.state

    return <Header
      isLoading={this.state.isLoading}
      title={title}
      collection={collection}
      startDate={startDate}
      endDate={endDate}
      total={count}
      showing={rows.length}
    />
  }

  Configure = () => {
    const { selectedFields, fields } = this.state
    const [open, setOpen] = useState(false)

    const handleApply = selectedFields => {
      this.setState({ selectedFields }, this.loadData)
      setOpen(false)
    }

    return <>
      {open ? <Configure
        fields={selectedFields}
        allFields={fields}
        onApply={handleApply}
        onCancel={() => setOpen(false)}
      /> : <>
        <Button variant='outline' size="lg" onClick={() => setOpen(true)}>
          Configure
        </Button>
      </>}
    </>
  }

  Table = () => {
    const { columns } = this

    return <DefaultTableResize
      columns={columns}
      data={this.state.rows}
    />
  }

  Scroll = () => {
    if (!this.hasNextPage) {
      return null
    }

    const handleNextPage = () => {
      if (this.state.isLoading) {
        return
      }

      this.setState(({ options }) => {
        options.size += options.size
        return { options }
      }, this.loadData)
    }

    return <div className="flex w-full justify-center items-center h-14">
      <InfiniteScroll onVisit={handleNextPage} hasMore={this.hasNextPage} />
    </div>
  }

  Content = () => {
    const { customFields: { getFields } } = useGlobalStore()
    const { fields: { options, default: selectedFields }, state: { fields }, metrics } = this
    const { setMenuCollapsed } = useAppLayoutContext()
    setMenuCollapsed(true)

    const createState = () => {
      try {
        const { filters, startDate: start, endDate: end, metric } = this.parseURL()

        const { field, value, label } = metrics[metric]
        if (field && !filters.hasOwnProperty(field)) {
          filters[field] = Array.isArray(value) ? value : [value]
        }

        const title = `Drilldown: ${label}`

        const startDate = moment.tz(moment.parseZone(start).format('YYYY-MM-DD HH:mm:ss'), appStore.timezone)
        const endDate = moment.tz(moment.parseZone(end).format('YYYY-MM-DD HH:mm:ss'), appStore.timezone)

        this.setState({
          filters: { filterby: filters, startDate, endDate },
          title,
        })

        history.replace({ search: '' })
      } catch (e) {
        history.push('/reports')
      }
    }

    const loadCustomFields = async () => {
      if (this.ignoreCustomFields) {
        this.setState({ fields: options })
        return
      }

      const customFields = await getFields()

      const customFieldsOptions = []
      customFields.forEach(({ key, name }) => {
        customFieldsOptions.push(
          { value: `cf_${key}`, label: name, group: "Custom Fields" },
          { value: `cf_${key}_trustedform`, label: name, group: "Custom Fields (Trustedform)" },
          { value: `cf_${key}_leadid`, label: name, group: "Custom Fields (Leadid)" },
        )
      })

      this.setState({
        fields: options.concat(customFieldsOptions),
      })
    }

    useEffect(() => {
      createState()
      loadCustomFields()
      this.setState({ selectedFields })
    }, [])

    useEffect(() => {
      if (fields.length) {
        this.loadData()
      }
    }, [fields])

    return <>
      <Helmet title={this.state.title} />
      <this.HeaderWrapper />
      <this.Actions />
      <ReportBody className='max-h-[calc(100vh-195px)]'>
        <this.Table />
        <this.Scroll />
      </ReportBody>
    </>
  }

  render = () => <this.Content />
}

const dateFormat = { hour: 'MM/DD/YYYY [at] hh:mm A', day: 'MM/DD/YYYY', month: 'MM/YYYY' }
